/** The query param View Orders runs as a search on each navigation that carries it. */
export const VIEW_ORDERS_SEARCH_PARAM = 'search';
/** The order View Orders shows, read again on a reload or Back without searching [SSD-7226 D4]. */
export const VIEW_ORDERS_ORDER_PARAM = 'order';
/**
 * A returned parcel's waybill beside the order: View Orders reopens the order in return mode, with the outbound
 * and inbound actions held. Arrival Sort links straight to it for a parcel that came back [SSD-7527].
 */
export const VIEW_ORDERS_RETURN_PARAM = 'return';
/** The Inventory sub-tab the address opens on; without it the page lands on Current Stocks [SSD-7444]. */
/** The Orders list with its Add marketing orders modal open — where the retired Marketing & Samples screen sends its links [SSD-7448]. */
export const ORDERS_ADD_ORDERS_PARAM = 'addOrders';
export const INVENTORY_TAB_PARAM = 'tab';
/**
 * The Stock History key and term the address carries. A Stock History search only reads, so the page runs it
 * whenever the address carries one: a reload, Back, a bookmark or a link from another screen [SSD-7444].
 */
export const STOCK_HISTORY_FIELD_PARAM = 'field';
export const STOCK_HISTORY_QUERY_PARAM = 'q';
/**
 * A Current Stocks search term the address opens with, typed into the search box once and then dropped from the
 * address: a link from the Manual Stock warning lands on the sku it names [SSD-7527]. Not the Stock History `q`.
 */
export const INVENTORY_FIND_PARAM = 'find';

export const ROUTES = {
  home: '/',
  login: '/login',
  /* Where the identity provider returns after a staff sign-in; registered as a redirect URI in Logto */
  authCallback: '/callback',
  /* Temporary: Bootstrap/shadcn coexistence proof for the portal-merge spike */
  uiSpike: '/ui-spike',
  /* The migrated marketing admin owns everything under /marketing via its own nested router */
  marketing: {
    wildcard: '/marketing/*',
    influencers: '/marketing',
    brandManager: '/marketing/brand-manager',
    campaignMatcher: '/marketing/campaign-matcher',
    community: '/marketing/community',
    templateManager: '/marketing/template-manager',
    affiliate: '/marketing/affiliate',
  },
  competitor: {
    default: '/competitor',
    v2: '/competitor/matching-v2',
    matchSKU: {
      default: '/competitor/sku',
      detail: (id?: string) => (id ? `/competitor/sku/${id}` : '/competitor/sku'),
    },
    matchStatus: '/competitor/match-status',
  },
  ai: {
    default: '/ai',
    commandCenter: '/ai/command-center',
    /** One agent's drill-down. A real URL so back, refresh and a shared link all land on the card. */
    commandCenterAgent: (cardId?: string) =>
      cardId ? `/ai/command-center/${encodeURIComponent(cardId)}` : '/ai/command-center',
    agentRegistry: '/ai/agent-registry',
    csPlayground: '/ai/cs-playground',
    knowledgeBase: '/ai/knowledge-base',
    recommendationLab: '/ai/recommendation-lab',
    skinMatchDemo: '/ai/skin-match-demo',
    skinAnalysisConfig: '/ai/skin-analysis-config',
  },
  oms: {
    default: '/oms',
    orders: '/oms/orders',
    orderDetail: (id?: string) => (id ? `/oms/orders/${id}` : '/oms/orders'),
    orderAudit: (id: string) => `/oms/orders/${id}/audit`,
    deliveryTrace: (id: string, trackingNo: string) => `/oms/orders/${id}/trace/${trackingNo}`,
    fulfillment: '/oms/fulfillment',
    picManagement: '/oms/pic-management',
    gigWorkers: '/oms/gig-workers',
    shippingFee: '/oms/shipping-fee',
    customsNamePrompt: '/oms/customs-name-prompt',
    gigCartVerificationPrompt: '/oms/gig-cart-verification-prompt',
    bundleRecipes: '/oms/bundle-recipes',
    zplPrint: '/oms/zpl-print',
    wms: {
      default: '/oms/wms',
      viewOrders: '/oms/wms/view-orders',
      stock: '/oms/wms/stock',
      readyToBeOutbonded: '/oms/wms/stock/readyToBeOutbonded',
    },
  },
  /* V2 rebuild — its own namespace, deliberately not nested under /oms */
  fulfillment: {
    default: '/fulfillment/orders',
    orders: '/fulfillment/orders',
    orderDetail: (id?: string | number) => (id != null ? `/fulfillment/orders/${id}` : '/fulfillment/orders'),
    ordersAddOrders: `/fulfillment/orders?${ORDERS_ADD_ORDERS_PARAM}=1`,
    /** The center's numbers in one place: Today, the logistics report and the monthly review (SSD-7602). */
    dashboard: '/fulfillment/dashboard',
    closing: '/fulfillment/closing',
    viewOrders: '/fulfillment/viewOrders',
    /**
     * View Orders with `code` already searched, the same as typing it into the search box. Only for a code
     * whose kind is unknown (a waybill, a barcode): the search can receive a product or park a parcel.
     */
    viewOrdersSearch: (code: string | number) =>
      `/fulfillment/viewOrders?${VIEW_ORDERS_SEARCH_PARAM}=${encodeURIComponent(String(code))}`,
    /** View Orders showing a known order: it is read, never searched, so the link is safe to open twice [SSD-7296]. */
    viewOrdersOrder: (orderId: string | number) =>
      `/fulfillment/viewOrders?${VIEW_ORDERS_ORDER_PARAM}=${encodeURIComponent(String(orderId))}`,
    /**
     * View Orders showing a known order in return mode, the parcel's waybill beside it: where a returned parcel
     * goes back on a shelf, a quantity and a shelf per line, in place of the Returns desk [SSD-7527]. View Orders
     * opens return mode only on an outbound waybill the order carries.
     */
    viewOrdersReturn: (orderId: string | number, waybill: string) =>
      `/fulfillment/viewOrders?${VIEW_ORDERS_ORDER_PARAM}=${encodeURIComponent(String(orderId))}&${VIEW_ORDERS_RETURN_PARAM}=${encodeURIComponent(waybill)}`,
    /** The door desk: scan an arrived parcel and read whether its order can be packed today (SSD-7326). */
    arrivalSort: '/fulfillment/arrivalSort',
    readyToOutbound: '/fulfillment/readyToOutbound',
    /** The packing desk: scan the PACKING barcode, then every product, before Zero Packing (SSD-7528). */
    packingCheck: '/fulfillment/packingCheck',
    inventory: '/fulfillment/inventory',
    /** Inventory's Stock History with `sku` already searched, the sku's stock, shelves and movements [SSD-7444]. */
    /** Inventory's Current Stocks with `term` already in the search box, as if typed [SSD-7527]. */
    inventoryFind: (term: string) => `/fulfillment/inventory?${INVENTORY_FIND_PARAM}=${encodeURIComponent(term)}`,
    stockHistorySku: (sku: string) =>
      `/fulfillment/inventory?${INVENTORY_TAB_PARAM}=history&${STOCK_HISTORY_FIELD_PARAM}=SKU&${STOCK_HISTORY_QUERY_PARAM}=${encodeURIComponent(sku)}`,
    unrecognizedScans: '/fulfillment/unrecognizedScans',
    /** Marketing orders whose waybill a person has to cancel at the carrier (SSD-7586); `order` marks one row. */
    manualCarrierCancels: '/fulfillment/manualCarrierCancels',
    manualCarrierCancelsOrder: (orderId: string | number) =>
      `/fulfillment/manualCarrierCancels?order=${encodeURIComponent(String(orderId))}`,
    /** The retired Returns desk [SSD-7527]: the address stays only so an old link lands on View Orders. */
    returns: '/fulfillment/returns',
    /** The retired Stock Move desk [SSD-7527]: the address stays only so an old link lands on Inventory. */
    stockMove: '/fulfillment/stockMove',
    /** The retired Stock Take screen [SSD-7447]: the address stays only so an old link lands on Inventory. */
    stockTake: '/fulfillment/stockTake',
    /** Retired screen: it opens the Orders list with the Add marketing orders modal [SSD-7448]. */
    marketing: '/fulfillment/marketing',
    inboundRequests: '/fulfillment/inboundRequests',
    inboundRequestDetail: (docId?: string) =>
      docId ? `/fulfillment/inboundRequests/${encodeURIComponent(docId)}` : '/fulfillment/inboundRequests',
    packingInvoice: (id?: string | number) =>
      id != null ? `/fulfillment/orders/${id}/packingInvoice` : '/fulfillment/orders',
  },
  sourcing: {
    default: '/sourcing',
    matching: '/sourcing/matching',
    matchingWithGlowpickId: (glowpickId?: string) =>
      glowpickId ? `/sourcing/matching/glowpickId/${glowpickId}` : '/sourcing/matching',
    matchingWithBrand: (brand?: string) =>
      brand ? `/sourcing/matching/brand/${encodeURIComponent(brand)}` : '/sourcing/matching',
    matchingWithCategory: (category?: string, subcategory?: string) =>
      category
        ? `/sourcing/matching/category/${encodeURIComponent(category)}${subcategory ? `/subcategory/${encodeURIComponent(subcategory)}` : ''}`
        : '/sourcing/matching',
    matched: '/sourcing/matched',
    matchedWithCategory: (category?: string, subcategory?: string) =>
      category
        ? `/sourcing/matched/category/${encodeURIComponent(category)}${subcategory ? `/subcategory/${encodeURIComponent(subcategory)}` : ''}`
        : '/sourcing/matched',
    matchedGpi: (gpi?: string) => (gpi ? `/sourcing/matched/gpi/${gpi}` : '/sourcing/matched'),
    matchedDetail: (cpProductId?: string, cpItemId?: string) =>
      cpProductId && cpItemId ? `/sourcing/matched/${cpProductId}/${cpItemId}` : '/sourcing/matched',
    matchedImageEditor: (cpProductId?: string, cpItemId?: string, tab?: 'thu' | 'pdp') =>
      cpProductId && cpItemId
        ? `/sourcing/matched/${cpProductId}/${cpItemId}/image-editor${tab ? `/${tab}` : '/thu'}`
        : '/sourcing/matched',
    ssReMatching: '/sourcing/ss-cp-rematching',
    scrapeBatch: '/sourcing/scrape-batch',
    coupangFailures: '/sourcing/coupang-failures',
    categoryMapping: '/sourcing/category-mapping',
  },
  banner: {
    default: '/site-management/banner',
    upload: '/site-management/banner/upload',
    list: '/site-management/banner/list',
  },
  trendingVideo: {
    default: '/site-management/trending-videos',
    upload: '/site-management/trending-videos/upload',
    list: '/site-management/trending-videos/list',
  },
  review: {
    default: '/review',
  },
  survey: {
    default: '/survey',
    settings: (surveyId?: string | number) => (surveyId ? `/survey/${surveyId}/settings` : '/survey'),
    quiz: (surveyId?: string | number) => (surveyId ? `/survey/${surveyId}/quiz` : '/survey'),
    statistics: (surveyId?: string | number) => (surveyId ? `/survey/${surveyId}/statistics` : '/survey'),
    answers: (surveyId?: string | number) => (surveyId ? `/survey/${surveyId}/answers` : '/survey'),
  },
  questionnaire: {
    default: '/questionnaire',
  },
  catalog: {
    default: '/catalog',
    ssEditor: '/catalog/ss-editor',
    ssEditorWithSku: (sku: string) => `/catalog/ss-editor/sku/${sku}`,
    cmWorkflow: '/catalog/cm-workflow',
    productTagMapping: '/catalog/product-tag-mapping',
    productGroups: {
      list: '/catalog/product-groups',
      new: '/catalog/product-groups/new',
      edit: (groupId: string) => `/catalog/product-groups/${groupId}`,
    },
  },
  siteManagement: {
    default: '/site-management',
    cache: '/site-management/cache',
    campaigns: '/site-management/campaigns',
    shipping: '/site-management/shipping',
    ruleEngine: '/site-management/rule-engine',
  },
  systemManagement: {
    default: '/system-management',
    cronJobs: '/system-management/cron-jobs',
    redirectConfiguration: '/system-management/redirect-configuration',
  },
  category: {
    default: '/category',
    ingredient: {
      default: '/category/ingredient',
    },
  },
  customer: {
    default: '/customer',
    loyalty: '/customer/loyalty',
    tierManagement: '/customer/loyalty/tier-management',
    pushNotification: '/customer/push-notification',
    csPlayground: '/customer/csPlayground',
    csPromptEditor: '/customer/csPromptEditor',
    knowledgeBase: '/customer/knowledgeBase',
  },
  agentTelemetry: {
    overview: '/ai/agent-telemetry',
    proposals: '/ai/agent-telemetry/proposals',
    proposalDetail: (proposalId?: string) =>
      proposalId ? `/ai/agent-telemetry/proposals/${encodeURIComponent(proposalId)}` : '/ai/agent-telemetry/proposals',
    roleAssets: '/ai/agent-telemetry/role-assets',
    sharedAssetHealth: '/ai/agent-telemetry/shared-asset-health',
    assetEvolution: '/ai/agent-telemetry/asset-evolution',
    roleAssetDetail: (assetId?: string) =>
      assetId ? `/ai/agent-telemetry/role-assets/${encodeURIComponent(assetId)}` : '/ai/agent-telemetry/role-assets',
    roleAssetsByRole: '/ai/agent-telemetry/role-assets/by-role',
    topList: (kind: string) => `/ai/agent-telemetry/top/${kind}`,
    employeeDetail: (employeeId?: string) =>
      employeeId ? `/ai/agent-telemetry/employees/${encodeURIComponent(employeeId)}` : '/ai/agent-telemetry',
    sessionDetail: (sessionId?: string) =>
      sessionId ? `/ai/agent-telemetry/sessions/${encodeURIComponent(sessionId)}` : '/ai/agent-telemetry',
  },
  claudeCode: {
    overview: '/claude-code',
    employeeDetail: (employeeId?: string) =>
      employeeId ? `/claude-code/employees/${encodeURIComponent(employeeId)}` : '/claude-code',
    sessionDetail: (sessionId?: string) =>
      sessionId ? `/claude-code/sessions/${encodeURIComponent(sessionId)}` : '/claude-code',
  },
  mcpAccess: {
    default: '/mcp-access',
    people: '/mcp-access/people',
    /** The people page opened on one tab: team roles, or keys. */
    peopleTab: (tab: 'roles' | 'keys') => `/mcp-access/people?tab=${tab}`,
    /** Old address of the roles page; it now opens the roles tab of the people page. */
    members: '/mcp-access/members',
  },
  secrets: {
    list: '/secrets',
    // Kept outside /secrets/ so it never collides with a secret path.
    requests: '/secretRequests',
    detail: (path: string) => `/secrets/${path.split('/').map(encodeURIComponent).join('/')}`,
  },
  /* Brands we sell to, as opposed to the rest of this admin, which runs our own storefront */
  b2b: {
    brandOnboarding: '/b2b/brand-onboarding',
  },
};
